import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Card, CardContent, Stack, Typography } from "@altimateai/lego";
import { parseSortDirection } from "@/components/tables/types";
import { RelativeComponentLoader } from "@/components/loader";
import { useAppState } from "@/modules/app/useAppContext";
import { usePriorityOpportunitiesQuery } from "../hooks";
import {
  downloadGroupedOpportunitiesCSV,
  downloadImplementedOpportunitiesCSV,
  downloadOpportunitiesCSV,
} from "@/helpers/apis";
import {
  useDiscoverFilters,
  useDiscoverImplementedOpportunitiesQuery,
  useDiscoverOpportunitiesQuery,
  useDiscoverSummaryQuery,
} from "./hooks";
import DiscoverHeader from "./components/DiscoverHeader";
import DiscoverDateRange from "./components/DiscoverDateRange";
import DiscoverSummary from "./components/DiscoverSummary";
import DiscoverTableControls from "./components/DiscoverTableControls";
import { DiscoverEmptyState } from "./components/DiscoverEmptyState";
import OpportunitiesListWithFilter from "./components/opportunities/OpportunitiesListWithFilter";
import GroupsListWithFilter from "./components/groups/GroupsListWithFilter";
import { useRegisterOpportunityFilters } from "./components/opportunities/useRegisterOpportunityFilters";
import { PrioritySection } from "../components/priority";

// Allow-list the tab identifiers so a deep-linked `?tab=foo` (typo,
// stale bookmark, or a future tab name that hasn't shipped yet) falls
// back to the default Opportunities view instead of rendering an empty
// card body. Keep in sync with the `<TabsTrigger value=>` set in
// DiscoverTableControls.
const DISCOVER_TABS = ["priority", "opportunities", "implemented", "groups"] as const;
type DiscoverTab = (typeof DISCOVER_TABS)[number];

// Resolve the active tab from the URL. `defaultTab` is the instance-aware
// landing tab the caller computes (Priority only when the *selected
// instance* has priority opps — see DiscoverContentV2 — otherwise
// Opportunities), used whenever there's no valid explicit `?tab=`.
// An explicit `?tab=priority` is still honoured when the tenant has the tab
// (the user chose it), but downgraded to Opportunities for tenants without
// it. A `?tab=implemented` is downgraded to `defaultTab` when the
// Implemented tab is off (per-tenant ``enableImplementedTab`` flag).
const resolveTab = (
  raw: string | null,
  priorityEnabled: boolean,
  implementedEnabled: boolean,
  defaultTab: DiscoverTab
): DiscoverTab => {
  if (raw === "priority" && !priorityEnabled) return "opportunities";
  if (raw === "implemented" && !implementedEnabled) return defaultTab;
  return (DISCOVER_TABS as readonly string[]).includes(raw ?? "")
    ? (raw as DiscoverTab)
    : defaultTab;
};

export const DiscoverContentV2 = () => {
  const [searchParams] = useSearchParams();
  const { enablePriorityTab, enableImplementedTab } = useAppState();
  const priorityTabEnabled = enablePriorityTab ?? false;
  const implementedTabEnabled = enableImplementedTab ?? false;
  const rawTab = searchParams.get("tab");

  // The Priority tab is per-instance: the priority query is scoped to the
  // selected instance (see usePriorityOpportunitiesQuery `select`), so its
  // result count tells us whether the *current instance* has any priority
  // opps. Land on Priority only when it does; otherwise default to the All
  // tab — this prevents an empty Priority page for an instance that has no
  // prioritised opportunities. Only fetched when the tenant has the tab.
  const { data: priorityData, isLoading: isPriorityLoading } = usePriorityOpportunitiesQuery({
    enabled: priorityTabEnabled,
  });
  const instanceHasPriority = (priorityData?.length ?? 0) > 0;
  const defaultTab: DiscoverTab =
    priorityTabEnabled && instanceHasPriority ? "priority" : "opportunities";
  const tab = resolveTab(rawTab, priorityTabEnabled, implementedTabEnabled, defaultTab);

  // Does the resolved tab fall back to the async, count-driven `defaultTab`?
  // It does whenever there's no explicit, still-valid tab choice to honour:
  // no `?tab=`, an unknown `?tab=foo` (typo / stale bookmark), or
  // `?tab=implemented` on a tenant without the Implemented tab. An explicit,
  // valid `?tab=priority`/`opportunities`/`groups` is NOT count-driven and is
  // exempt. Mirrors the `resolveTab` branches that return `defaultTab`.
  const rawIsKnownTab = (DISCOVER_TABS as readonly string[]).includes(rawTab ?? "");
  const tabDependsOnDefault =
    !rawIsKnownTab || (rawTab === "implemented" && !implementedTabEnabled);

  // Hold the tab area behind a loader until the per-instance priority count is
  // known, but only when the landing tab is count-driven — so we neither flash
  // an empty Priority tab nor bounce Opportunities→Priority (including for an
  // unknown `?tab=`). `keepPreviousData` on the query keeps `isPriorityLoading`
  // false on subsequent filter changes, so this gate fires on first load only.
  const isResolvingDefaultTab = priorityTabEnabled && tabDependsOnDefault && isPriorityLoading;

  // Register the Discover-wide More-Filters chip set (Resource Type,
  // Resource Name, User, Warehouse, Workspace) at the page level so the
  // chips are addressable from every tab — not just Opportunities and
  // Implemented (where ``OpportunitiesListWithFilter`` mounts). Priority
  // and Groups tabs honour the chip values on the wire; without this
  // hoist, the user has no way to change or clear them from those tabs.
  useRegisterOpportunityFilters();

  // Sort state from search params (used by useDiscoverFilters for API calls)
  const sortAttribute = searchParams.get("sort") || "";
  const sortOrder = parseSortDirection(searchParams.get("sort_order"));

  // Get filter params from context
  const { filterParams, groupedFilterParams, isInitialized } = useDiscoverFilters({
    sortAttribute,
    sortOrder,
  });

  // Query hooks with context-based filter params
  const { data: opportunitiesData, isFetching: isOpportunitiesLoading } =
    useDiscoverOpportunitiesQuery({
      filterParams,
      isInitialized,
      // Don't fire the All-tab query during the brief default-tab resolve
      // window (it would race the priority count and may not be the tab we
      // land on).
      enabled: tab === "opportunities" && !isResolvingDefaultTab,
    });

  // Implemented opportunities live in a separate ClickHouse table — only
  // fetch when the user opens that tab to avoid an extra round-trip on
  // every page load.
  //
  // The implemented endpoint's request schema is narrower than the
  // regular one in exactly one dimension: `status` is invariant (the
  // table only holds IMPLEMENTED rows by construction), so we strip it
  // here to keep URL / persisted filter chips from appearing to do
  // something they can't.
  //
  // `user` and `warehouse` (and effort/resource/owner/opportunity/date/
  // instance) are forwarded as-is. The snapshot carries those columns
  // and the endpoint accepts them. User-scoped filtering on the
  // Implemented tab degrades to strict-deny for snapshots whose
  // contributions spanned multiple users — the backend stores NULL on
  // the attribution column in that case, which is the correct privacy
  // default for a system-wide implementation record.
  const implementedFilterParams = useMemo(() => {
    // Drop `status` — the Implemented tab is itself a status, so the
    // chip's value here is always implied. Workspace flows through:
    // `GetImplementedOpportunitiesRequest` now carries `workspace_id`
    // and the snapshot table reuses the same `resources` array shape
    // as the live table, so the repository's per-resource-type
    // inventory JOIN narrows snapshots to the selected workspaces.
    const { status: _droppedStatus, ...rest } = filterParams;
    return rest;
  }, [filterParams]);

  const {
    data: implementedOpportunitiesData,
    isFetching: isImplementedOpportunitiesLoading,
    isError: isImplementedOpportunitiesError,
    refetch: refetchImplementedOpportunities,
  } = useDiscoverImplementedOpportunitiesQuery({
    filterParams: implementedFilterParams,
    isInitialized,
    enabled: tab === "implemented",
  });

  const { data: summaryData, isFetching: isSummaryLoading } = useDiscoverSummaryQuery({
    filterParams,
    isInitialized,
  });

  // Download handlers
  const handleDownloadClick = useCallback(async () => {
    if (tab === "opportunities") {
      await downloadOpportunitiesCSV(filterParams);
    } else if (tab === "implemented") {
      await downloadImplementedOpportunitiesCSV(implementedFilterParams);
    } else {
      await downloadGroupedOpportunitiesCSV(groupedFilterParams);
    }
  }, [tab, filterParams, implementedFilterParams, groupedFilterParams]);

  return (
    <Stack direction={"column"} className={"al-w-full"}>
      <DiscoverHeader />
      <Card>
        <CardContent className="!al-p-4">
          {/* Filter row with DateRange and portals */}
          <Stack gap="md" align="center" justify="between" className="al-mb-4">
            <Stack gap="md" align="center">
              <DiscoverDateRange />
              <div id="discover-filter-container" />
            </Stack>
            <Stack gap="md" align="center">
              <div id="discover-saved-filters-container" />
            </Stack>
          </Stack>

          {/* Summary section */}
          <DiscoverSummary data={summaryData} isLoading={isSummaryLoading} />

          {/* Section heading + table controls (Tabs + Download + Column picker) */}
          <Typography variant="section-heading" className="al-mt-6 al-mb-4">
            Opportunities
          </Typography>

          {isResolvingDefaultTab ? (
            // Default landing, Priority tab enabled, per-instance priority
            // count still loading — hold here so we don't flash the wrong
            // tab (empty Priority or a momentary All→Priority bounce).
            <RelativeComponentLoader label="Loading opportunities" />
          ) : (
            <>
              <DiscoverTableControls
                tab={tab}
                onDownloadClick={handleDownloadClick}
              />

              {/* Table content based on active tab.
               *
               * Each tab renders the regular list component on every path so
               * the More-Filters / Saved-Filters portal children (mounted by
               * the DataTable inside the list) stay alive even when the
               * fetch settles with zero rows. The tab-specific empty state
               * is handed to the list as a prop and rendered in place of
               * the table body — replacing the list entirely would unmount
               * the filter UI and trap the user with no way to clear the
               * filter that produced the empty result. Loading still goes
               * through the list (skeleton rows) so the user doesn't see an
               * "empty" flash before the first row arrives.
               */}
              {tab === "priority" && <PrioritySection />}
              {tab === "opportunities" && (
                <OpportunitiesListWithFilter
                  data={opportunitiesData}
                  isLoading={isOpportunitiesLoading}
                  emptyState={<DiscoverEmptyState tab="opportunities" />}
                />
              )}
              {tab === "implemented" && (
                <>
                  {isImplementedOpportunitiesError && (
                    // Surface the fetch failure explicitly so the user
                    // doesn't mistake a 5xx for "you have no implemented
                    // opportunities". The retry button calls react-query's
                    // `refetch`, which re-runs the same query key without
                    // having to remount the component.
                    <div
                      role="alert"
                      aria-live="polite"
                      data-testid="implemented-fetch-error"
                      className="al-mb-md al-p-md al-bg-red-50 al-border al-border-red-200 al-rounded"
                    >
                      <Stack direction="row" gap="md" align="center">
                        <span>
                          Couldn&apos;t load implemented opportunities. The backend returned an
                          error.
                        </span>
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => refetchImplementedOpportunities()}
                        >
                          Retry
                        </Button>
                      </Stack>
                    </div>
                  )}
                  <OpportunitiesListWithFilter
                    data={implementedOpportunitiesData}
                    isLoading={isImplementedOpportunitiesLoading}
                    hideStatusFilter
                    // Suppress the empty-state copy on the error path — the
                    // banner above already explains the empty table and a
                    // second "no opportunities yet" message would imply the
                    // dataset is genuinely empty.
                    emptyState={
                      isImplementedOpportunitiesError ? undefined : (
                        <DiscoverEmptyState tab="implemented" />
                      )
                    }
                  />
                </>
              )}
              {tab === "groups" && (
                <GroupsListWithFilter emptyState={<DiscoverEmptyState tab="groups" />} />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};
